import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  BUDGET_QUESTION,
  BUDGET_REFINEMENT,
  BUDGETS,
  budgetLabels,
  budgetOutcome,
  budgetSummary,
  budgetTraces,
  budgetTransition,
  callsText,
  currentStep,
  DEFINITION_REFINEMENT,
  definitionSummary,
  definitionTransition,
  entryText,
  ERROR_REFINEMENT,
  errorSummary,
  errorTransition,
  FAILURES,
  failureLabels,
  failures,
  finishReason,
  initialBudget,
  initialDefinition,
  initialError,
  initialLoop,
  lastPromptTokens,
  LOOP_QUESTIONS,
  LOOP_REFINEMENT,
  loopAnswers,
  loopDone,
  loopQuestionLabels,
  loopSummary,
  loopTransition,
  mayaSees,
  NO_EXCEPTION,
  shownSteps,
  stepLabel,
  stepsUsed,
  toBudget,
  toFailure,
  toLoopQuestion,
  TOOL_SETS,
  toolRequests,
  toolSetLabels,
  toToolSet,
  type BudgetAction,
  type DefinitionAction,
  type ErrorAction,
  type LoopAction,
} from './tool-models';

import './relationship-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const TRACE_COLUMNS = ['Step', 'The model asks for', 'Our code sends back'];
const TRACE_TEMPLATE = 'minmax(0, 0.4fr) minmax(0, 2fr) minmax(0, 1.6fr)';
const REPLAY = 'This graphic replays real runs.';

export function ToolDefinitionLab() {
  const [state, setState] = useState(initialDefinition);
  const act = (action: DefinitionAction) =>
    setState((current) => definitionTransition(current, action));
  const request = toolRequests[state.set];
  const summary = definitionSummary(state);
  return (
    <Lab
      title="What does the request carry when tools are sent, and what does it cost?"
      eyebrow="Tools · the definition"
      className="rel-lab tool-lab"
    >
      <Predict>
        How many prompt tokens does the request carry? Choose the tools sent, then press Show the
        request.
      </Predict>
      <div className="cb-controls">
        <label>
          Tools sent
          <ModelSelect
            aria-label="Tools sent"
            value={state.set}
            onValueChange={(value) => act({ type: 'set', value: toToolSet(value) })}
            options={TOOL_SETS.map((set) => ({ value: set, label: toolSetLabels[set] }))}
          />
        </label>
        <Button size="sm" disabled={state.shown} onClick={() => act({ type: 'show' })}>
          Show the request
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="list_tasks as the model sees it">
        <pre aria-label="list_tasks as the model sees it">{entryText(request)}</pre>
      </Panel>
      {state.shown ? (
        <>
          <Panel label="The first reply">
            <pre aria-label="The first reply">{request.firstReply}</pre>
          </Panel>
          <Facts
            values={[
              ['Prompt tokens', String(request.promptTokens)],
              ['Tools sent', String(request.toolsSent)],
              ['First move', request.firstMove],
            ]}
          />
        </>
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{DEFINITION_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ToolLoopLab() {
  const [state, setState] = useState(initialLoop);
  const act = (action: LoopAction) => setState((current) => loopTransition(current, action));
  const step = currentStep(state);
  const done = loopDone(state);
  const summary = loopSummary(state);
  return (
    <Lab
      title="How many steps does this question take, and who runs each?"
      eyebrow="Tools · the loop"
      className="rel-lab tool-lab"
    >
      <Predict>
        How many steps does this question take? Choose a question, then press Next step until the
        model stops.
      </Predict>
      <div className="cb-controls">
        <label>
          Question
          <ModelSelect
            aria-label="Question"
            value={state.question}
            onValueChange={(value) => act({ type: 'question', value: toLoopQuestion(value) })}
            options={LOOP_QUESTIONS.map((question) => ({
              value: question,
              label: loopQuestionLabels[question],
            }))}
          />
        </label>
        <Button size="sm" disabled={done} onClick={() => act({ type: 'next' })}>
          Next step
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="Trace"
        columns={TRACE_COLUMNS}
        template={TRACE_TEMPLATE}
        rows={shownSteps(state).map((entry, index) => [
          index + 1,
          callsText(entry.calls),
          entry.result,
        ])}
      />
      <Panel label="The answer">
        <pre aria-label="The answer">
          {done ? loopAnswers[state.question] : '(the model has not stopped yet)'}
        </pre>
      </Panel>
      {step ? (
        <Facts
          values={[
            ['Step', stepLabel(state)],
            ['Prompt tokens', String(step.promptTokens)],
            ['finish_reason', step.finish],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {done ? <p className="rel-caption">{LOOP_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ToolErrorLab() {
  const [state, setState] = useState(initialError);
  const act = (action: ErrorAction) => setState((current) => errorTransition(current, action));
  const failure = failures[state.failure];
  const summary = errorSummary(state);
  return (
    <Lab
      title="What does the model do with a failed tool?"
      eyebrow="Tools · a failure is a result"
      className="rel-lab tool-lab"
    >
      <Predict>
        What does the model do with a failed tool? Choose a failure, then press Send the result.
      </Predict>
      <div className="cb-controls">
        <label>
          Failure
          <ModelSelect
            aria-label="Failure"
            value={state.failure}
            onValueChange={(value) => act({ type: 'failure', value: toFailure(value) })}
            options={FAILURES.map((kind) => ({ value: kind, label: failureLabels[kind] }))}
          />
        </label>
        <Button size="sm" disabled={state.sent} onClick={() => act({ type: 'send' })}>
          Send the result
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="cb-two">
        <Panel label="The request">
          <pre aria-label="The request">{failure.request}</pre>
        </Panel>
        <Panel label="The result the loop sent">
          <pre aria-label="The result the loop sent">{failure.result}</pre>
        </Panel>
      </div>
      <Panel label="The model's next move">
        <pre aria-label="The model's next move">
          {state.sent ? failure.reply : '(not sent yet)'}
        </pre>
      </Panel>
      {state.sent ? (
        <Facts
          values={[
            ['Next move', failure.nextMove],
            ['finish_reason', finishReason(failure)],
            ['Exception', NO_EXCEPTION],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.sent ? <p className="rel-caption">{ERROR_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ToolBudgetLab() {
  const [state, setState] = useState(initialBudget);
  const act = (action: BudgetAction) => setState((current) => budgetTransition(current, action));
  const trace = budgetTraces[state.budget];
  const summary = budgetSummary(state);
  return (
    <Lab
      title="Where does the loop stop, and what does Maya see?"
      eyebrow="Tools · the step budget"
      className="rel-lab tool-lab"
    >
      <Predict>Where does the loop stop? Choose a step budget, then press Run.</Predict>
      <div className="cb-controls">
        <label>
          Step budget
          <ModelSelect
            aria-label="Step budget"
            value={state.budget}
            onValueChange={(value) => act({ type: 'budget', value: toBudget(value) })}
            options={BUDGETS.map((budget) => ({ value: budget, label: budgetLabels[budget] }))}
          />
        </label>
        <Button size="sm" disabled={state.ran} onClick={() => act({ type: 'run' })}>
          Run
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The question">
        <pre aria-label="The question">{BUDGET_QUESTION}</pre>
      </Panel>
      <Table
        label="Trace"
        columns={TRACE_COLUMNS}
        template={TRACE_TEMPLATE}
        rows={
          state.ran
            ? trace.map((entry, index) => [index + 1, callsText(entry.calls), entry.result])
            : []
        }
      />
      <Panel label="What Maya sees">
        <pre aria-label="What Maya sees">
          {state.ran ? mayaSees(state.budget) : '(not run yet)'}
        </pre>
      </Panel>
      {state.ran ? (
        <Facts
          values={[
            ['Steps used', stepsUsed(state.budget)],
            ['Prompt tokens, last step', String(lastPromptTokens(trace))],
            ['Outcome', budgetOutcome(state.budget)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{BUDGET_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
